import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { getCapitalByStrategy, getCapitalByProduct } from '../../api/metrics';
import CapitalAllocationDonut from '../charts/CapitalAllocationDonut';
import StandaloneSection from '../shared/StandaloneSection';
import './CapitalAllocationChart.css';

/**
 * Capital Allocation Chart Component
 * Displays capital distribution by product and strategy type
 * Uses backend views (capital_deployment_by_strategy, capital_deployment_by_product)
 * instead of frontend aggregation for accuracy and performance
 */
const CapitalAllocationChart: React.FC = () => {
  // Fetch strategy allocation from backend view
  const { data: strategyData, isLoading: strategyLoading, isError: strategyError } = useQuery({
    queryKey: ['capital-by-strategy'],
    queryFn: getCapitalByStrategy,
    retry: 2,
  });

  // Fetch product allocation from backend view
  const { data: productData, isLoading: productLoading, isError: productError } = useQuery({
    queryKey: ['capital-by-product'],
    queryFn: getCapitalByProduct,
    retry: 2,
  });

  // Dynamic color palette - supports any number of strategies/products
  const COLOR_PALETTE = [
    '#8a8ced', // Purple (default first)
    '#06b6d4', // Cyan
    '#ec4899', // Pink
    '#8b5cf6', // Violet
    '#f59e0b', // Amber
    '#10b981', // Emerald
    '#ef4444', // Red
    '#f97316', // Orange
    '#341875', // Deep Purple
    '#9fe2da', // Teal
  ];

  const formatStrategyName = (name: string | null | undefined) => {
    if (!name) return 'Unknown';
    return name.split('_').map(word =>
      word.charAt(0) + word.slice(1).toLowerCase()
    ).join(' ');
  };

  // Transform strategy data for donut chart
  // Filter out strategies with zero or negative allocated capital
  const strategyAllocation = strategyData?.strategies
    .filter(strategy => strategy.allocatedCapital > 0)
    .map((strategy, index) => ({
      name: formatStrategyName(strategy.strategyType),
      value: strategy.allocatedCapital,
      color: COLOR_PALETTE[index % COLOR_PALETTE.length]
    })) || [];

  // Transform product data for donut chart (top 10)
  // Filter out products with zero or negative allocated capital
  const productAllocation = productData?.products
    .filter(product => product.allocatedCapital > 0)
    .slice(0, 10)
    .map((product, index) => ({
      name: product.productId,
      value: product.allocatedCapital,
      color: COLOR_PALETTE[index % COLOR_PALETTE.length]
    })) || [];

  const isLoading = strategyLoading || productLoading;
  const isError = strategyError || productError;

  if (isLoading) {
    return (
      <StandaloneSection title="CAPITAL ALLOCATION">
        <div className="chart-loading">
          <div className="loading-spinner"></div>
          <p>Loading allocation data...</p>
        </div>
      </StandaloneSection>
    );
  }

  if (isError) {
    return (
      <StandaloneSection title="CAPITAL ALLOCATION">
        <div className="error-message">
          <p>Unable to load capital allocation data</p>
        </div>
      </StandaloneSection>
    );
  }

  return (
    <StandaloneSection title="CAPITAL ALLOCATION">
      <div className="allocation-charts">
        <div className="allocation-section">
          <h3>By Product (Top 10)</h3>
          {productAllocation.length > 0 ? (
            <CapitalAllocationDonut data={productAllocation} />
          ) : (
            <div className="no-data">No capital allocated</div>
          )}
        </div>

        <div className="allocation-section">
          <h3>By Strategy Type</h3>
          {strategyAllocation.length > 0 ? (
            <CapitalAllocationDonut data={strategyAllocation} />
          ) : (
            <div className="no-data">No capital allocated</div>
          )}
        </div>
      </div>
    </StandaloneSection>
  );
};

export default CapitalAllocationChart;
